import { useForm } from '@tanstack/react-form'
import { Button } from '@workspace/ui/components/button'
import { Checkbox } from '@workspace/ui/components/checkbox'
import { ErrorBanner } from '@workspace/ui/components/error-banner'
import { FormField } from '@workspace/ui/components/form-field'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { ProfileHeader } from '@workspace/ui/components/profile-header'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import { TextInput } from '@workspace/ui/components/text-input'
import { formatFileSize } from '@workspace/ui/lib/utils'
import { Check, FileText, Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { DocumentUpload } from '../../../components/document-upload'
import {
  useInvestorConfigQuery,
  useInvestorQuery,
  useUpdateInvestorMutation,
} from '../../../hooks/api'
import { useImageUpload } from '../../../hooks/use-image-upload'

type InvestorEditProps = {
  onExitEdit: () => void
}

export function InvestorEdit({ onExitEdit }: InvestorEditProps) {
  const { data: investorResponse, isLoading, error } = useInvestorQuery()
  const { data: investorConfigResponse } = useInvestorConfigQuery()

  const investor = investorResponse?.data
  const investmentAreasOptions = investorConfigResponse?.data?.investmentAreas ?? []
  const investmentRegionsOptions = investorConfigResponse?.data?.investmentRegions ?? []

  const [selectedAreas, setSelectedAreas] = useState<string[]>([])
  const [selectedRegions, setSelectedRegions] = useState<string[]>([])
  const [thesisDocumentId, setThesisDocumentId] = useState<string | null>(null)
  const [pendingThesisDocument, setPendingThesisDocument] = useState<{
    id: string
    fileName: string
  } | null>(null)
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null)

  const logoUpload = useImageUpload({
    onSuccess: (url) => setLogoUrl(url),
  })
  const coverUpload = useImageUpload({
    onSuccess: (url) => setCoverImageUrl(url),
  })

  const updateInvestorMut = useUpdateInvestorMutation({
    onSuccess: () => onExitEdit(),
  })

  const getFormValues = (data?: typeof investor) => ({
    name: data?.name ?? '',
    websiteUrl: data?.websiteUrl ?? '',
  })

  const form = useForm({
    defaultValues: getFormValues(investor),
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: getFormValues is a stable factory, investor?.id guards re-runs
  useEffect(() => {
    if (!investor) return
    setSelectedAreas(investor.investmentAreas ?? [])
    setSelectedRegions(investor.investmentGeographies ?? [])
    setThesisDocumentId(investor.thesisDocumentId ?? null)
    setLogoUrl(investor.logoUrl ?? null)
    setCoverImageUrl(investor.coverImageUrl ?? null)
    form.reset(getFormValues(investor))
  }, [investor?.id, form.reset, investor])

  const toggleArea = (value: string) => {
    setSelectedAreas((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  const toggleRegion = (value: string) => {
    setSelectedRegions((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  const handleRemoveThesis = () => {
    setThesisDocumentId(null)
    setPendingThesisDocument(null)
  }

  const handleCancel = () => {
    form.reset(getFormValues(investor))
    setSelectedAreas(investor?.investmentAreas ?? [])
    setSelectedRegions(investor?.investmentGeographies ?? [])
    setThesisDocumentId(investor?.thesisDocumentId ?? null)
    setPendingThesisDocument(null)
    setLogoUrl(investor?.logoUrl ?? null)
    setCoverImageUrl(investor?.coverImageUrl ?? null)
    onExitEdit()
  }

  const handleSave = () => {
    if (!investor) return
    const values = form.state.values

    updateInvestorMut.mutate({
      path: { id: investor.id },
      body: {
        name: values.name || undefined,
        websiteUrl: values.websiteUrl || null,
        logoUrl: logoUrl,
        coverImageUrl: coverImageUrl,
        investmentAreas: selectedAreas,
        investmentGeographies: selectedRegions,
        thesisDocumentId: thesisDocumentId,
      },
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !investor) {
    return (
      <div className="flex items-center justify-center py-20">
        <ErrorBanner message={error?.message || 'Failed to load investor profile'} />
      </div>
    )
  }

  const currentThesisDocument = pendingThesisDocument ?? investor.thesisDocument

  return (
    <>
      <ProfileHeader
        coverImageUrl={coverImageUrl ?? investor.coverImageUrl}
        avatarUrl={logoUrl ?? investor.logoUrl}
        name={investor.name}
        isEditMode
        onCoverImageChange={(file) => coverUpload.upload(file)}
        onAvatarChange={(file) => logoUpload.upload(file)}
        isCoverUploading={coverUpload.isUploading}
        isAvatarUploading={logoUpload.isUploading}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={handleCancel}>
              <X className="size-4" />
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={updateInvestorMut.isPending}>
              <Check className="size-4" />
              {updateInvestorMut.isPending ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
        contentClassName="mx-auto max-w-6xl"
      />

      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <form.Field name="name">
            {(field) => (
              <FormField label="Entity Name" required>
                <TextInput
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="e.g., Sequoia Capital"
                />
              </FormField>
            )}
          </form.Field>
          <form.Field name="websiteUrl">
            {(field) => (
              <FormField label="Website URL">
                <TextInput
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="https://example.com"
                />
              </FormField>
            )}
          </form.Field>
        </div>

        <ProfileSection title="Investment Focus">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {investmentAreasOptions.map((area) => (
              <div
                key={area.value}
                className="flex items-center gap-2 p-3 rounded-lg border border-border bg-card cursor-pointer hover:bg-accent/50 transition-colors"
              >
                <Checkbox
                  id={`area-${area.value}`}
                  checked={selectedAreas.includes(area.value)}
                  onCheckedChange={() => toggleArea(area.value)}
                />
                <label htmlFor={`area-${area.value}`} className="text-sm cursor-pointer">
                  {area.label}
                </label>
              </div>
            ))}
          </div>
        </ProfileSection>

        <ProfileSection title="Investment Geographies">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {investmentRegionsOptions.map((region) => (
              <div
                key={region.value}
                className="flex items-center gap-2 p-3 rounded-lg border border-border bg-card cursor-pointer hover:bg-accent/50 transition-colors"
              >
                <Checkbox
                  id={`region-${region.value}`}
                  checked={selectedRegions.includes(region.value)}
                  onCheckedChange={() => toggleRegion(region.value)}
                />
                <label htmlFor={`region-${region.value}`} className="text-sm cursor-pointer">
                  {region.label}
                </label>
              </div>
            ))}
          </div>
        </ProfileSection>

        <ProfileSection title="Investment Thesis">
          {thesisDocumentId && currentThesisDocument ? (
            <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
              <div className="flex items-center gap-3">
                <FileText className="size-8 text-muted-foreground" />
                <div>
                  <p className="font-medium text-sm">{currentThesisDocument.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {!pendingThesisDocument && investor?.thesisDocument?.fileSize
                      ? formatFileSize(investor.thesisDocument.fileSize)
                      : 'Document'}
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleRemoveThesis}>
                <Trash2 className="size-4" />
                Remove
              </Button>
            </div>
          ) : (
            <DocumentUpload
              onUploadComplete={(documentId, file) => {
                setPendingThesisDocument({ id: documentId, fileName: file.name })
                setThesisDocumentId(documentId)
              }}
            />
          )}
        </ProfileSection>
      </div>
    </>
  )
}
